import axios from "axios";
import { ApiSuccess, saUrl } from "./api_helper";
import type { PaginatedMeta } from "./super_admin_plans_helper";

/** Janela agregada do dashboard SA (DOC 02 § 6.1). */
export type SaMetricsWindow = {
  from: string;
  to: string;
  sends_count: number;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  failed: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  complaint_rate: number;
  delivery_rate: number;
};

/** Lag aproximado da fila Redis `send` (MVP sem Horizon — LAC-042). */
export type SaQueueSnapshot = {
  send_pending: number | null;
  /** Jobs P0 (API/transacional) ainda na fila `send`. */
  send_p0_pending?: number | null;
  /** Fila `import` (CSV / higiene). */
  import_pending?: number | null;
  failed_jobs: number | null;
  /** Heartbeat do worker (LAC-028 polish). */
  worker_ok?: boolean | null;
  worker_heartbeat_at?: string | null;
  source: string;
};

export type SaSesAccountSnapshot = {
  available: boolean;
  region?: string | null;
  enforcement_status?: string | null;
  sending_enabled?: boolean | null;
  production_access_enabled?: boolean | null;
  sent_last_24h?: number | null;
  max_24h_send?: number | null;
  max_send_rate?: number | null;
  checked_at?: string | null;
  error?: string | null;
};

export type SaDashboardMetrics = {
  generated_at: string;
  window_24h: SaMetricsWindow;
  window_7d: SaMetricsWindow;
  tenants_total: number;
  tenants_blocked: number;
  tenants_at_risk: number;
  /** Tenants com email_circuit_open_at preenchido. */
  circuits_open_count?: number;
  /** Disparos pausados 2ª+ vez — só SA retoma. */
  sends_awaiting_sa_resume?: number;
  /** IDs recentes (até 5) para copiar no alerta. */
  awaiting_sa_resume_send_ids?: number[];
  /** Canary paused count (LAC-092). */
  sends_canary_paused?: number;
  /** Tenants near daily cap (LAC-097). */
  tenants_at_daily_cap?: number;
  platform_freeze: boolean | null;
  platform_freeze_at?: string | null;
  /** cooldown | rates_breaching | ready | disabled */
  platform_freeze_hold_reason?: string | null;
  platform_freeze_auto_clear_eta_at?: string | null;
  platform_freeze_auto_clear_enabled?: boolean;
  /** Espelho AWS GetAccount (cache). */
  ses_account?: SaSesAccountSnapshot | null;
  queue?: SaQueueSnapshot;
  email_suppressions_count: number;
  domain_suppressions_count: number;
  mrr_cents: number;
  mrr_currency: string;
  /** LAC-099: billing gateway status. */
  billing?: {
    mp_webhook_secret_configured: boolean;
    mp_webhook_secret_required: boolean;
    mp_webhook_secret_alert: boolean;
  };
  /** Login Google Client ID (LAC-045) — alerta só em production. */
  google_login?: {
    client_id_configured: boolean;
    client_id_required: boolean;
    client_id_alert: boolean;
  };
  /** LAC-139: circuit digest snapshot. */
  circuit_digest?: {
    circuits_open: number;
    awaiting_sa: number;
    auto_closed_24h: number;
    second_pause_notified_24h: number;
    platform_frozen: boolean;
  };
  /** LAC-092/097: menu badges. */
  menu_badges?: {
    sends_awaiting_sa_resume: number;
    sends_canary_paused: number;
    circuits_open: number;
    tenants_at_risk: number;
    tenants_at_daily_cap: number;
    host_stale: boolean;
    host_warn: boolean;
    dns_glue_ok: boolean;
    mp_webhook_secret_alert: boolean;
    google_client_id_alert?: boolean;
  };
  /** DNS glue check (auth NS → IP esperado). */
  dns_glue?: {
    ok: boolean;
    expected_ipv4?: string | null;
    /** Alias legado — preferir expected_ipv4. */
    expected_ip?: string;
    nameservers?: Array<{
      host?: string;
      ns?: string;
      addresses?: string[];
      ip?: string | null;
      ok: boolean;
      error?: string | null;
    }>;
    checked_at?: string;
  };
};

/** Snapshot VPS (CPU/RAM/disco) — GET /super-admin/ops/host-metrics. */
export type SaHostMetricsCurrent = {
  cpu_pct: number;
  ram_used_mb: number;
  ram_total_mb: number;
  ram_used_pct: number;
  disk_used_gb: number;
  disk_total_gb: number;
  disk_used_pct: number;
  load1?: number | null;
  sampled_at: string;
};

export type SaHostMetricsPeak = {
  cpu_pct: number;
  cpu_at?: string | null;
  ram_used_pct: number;
  ram_used_mb: number;
  ram_total_mb: number;
  ram_at?: string | null;
  disk_used_pct: number;
  disk_used_gb: number;
  disk_total_gb: number;
  disk_at?: string | null;
};

export type SaHostMetricsSeriesPoint = {
  sampled_at: string;
  cpu_pct: number;
  ram_used_pct: number;
  disk_used_pct: number;
};

export type SaHostMetrics = {
  available: boolean;
  current: SaHostMetricsCurrent | null;
  peak_24h: SaHostMetricsPeak | null;
  above_warn_pct?: { cpu: number; ram: number; disk: number } | null;
  sample_count_24h: number;
  /** Pontos recentes para sparkline no dashboard SA. */
  series?: SaHostMetricsSeriesPoint[];
  thresholds: {
    cpu_pct_warn: number;
    cpu_pct_crit: number;
    ram_pct_warn: number;
    ram_pct_crit: number;
    disk_pct_warn: number;
    disk_pct_crit: number;
  };
  netdata_url?: string | null;
  stale: boolean;
  /** true enquanto o cron/agente ainda não coletou após “Atualizar agora”. */
  sample_requested?: boolean;
};

export type SaCampaignSend = {
  id: number;
  tenant_id: number;
  tenant?: { id: number; name: string; blocked: boolean } | null;
  campaign_id: number;
  campaign?: { id: number; name: string } | null;
  status: string;
  pause_reason?: "canary" | "circuit" | "platform" | "deadline" | "partial_chain" | string | null;
  circuit_pause_count?: number;
  needs_sa_resume?: boolean;
  /** 1ª pausa: ISO UTC estimada de auto-retomada. */
  circuit_auto_resume_eta_at?: string | null;
  canary_pilot_size?: number | null;
  pending_count?: number;
  priority_band?: "p1" | "p2" | string | null;
  waiting_for_capacity?: boolean;
  is_test: boolean;
  subject: string;
  queued: number;
  sent: number;
  bounced: number;
  complained: number;
  failed: number;
  bounce_rate: number;
  complaint_rate: number;
  scheduled_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  created_at?: string;
  partial_sample?: boolean;
  partial_day_index?: number;
  partial_root_send_id?: number;
  continuation_of_send_id?: number;
  partial_chain_capped?: boolean;
};

export type SaEmailSuppression = {
  id: number;
  email: string;
  reason: string | null;
  source: string | null;
  suppressed_at?: string | null;
  created_at?: string;
};

export type SaDomainSuppression = {
  id: number;
  domain: string;
  reason: string | null;
  source: string | null;
  evidence?: Record<string, unknown> | null;
  mailbox_provider?: boolean;
  suppressed_at?: string | null;
  created_at?: string;
};

export type SaTenantListItem = {
  id: number;
  name: string;
  billing_channel?: "self_serve" | "brdominio" | string;
  preferred_billing_currency?: "BRL" | "USD" | string | null;
  blocked: boolean;
  blocked_at?: string | null;
  from_email?: string | null;
  ses_identity_status?: string | null;
  tracking_status?: string | null;
  sending_setup_ready: boolean;
  sending_stage?: string;
  sent_today?: number;
  daily_cap?: number | null;
  /** true quando sent_today ≥ ~90% do cap efetivo de ramp (LAC-088). */
  at_daily_cap?: boolean;
  /** Cert emitido (ssl_ok) mas SNI/HTTPS ainda falha. */
  tracking_ssl_attention?: boolean;
  created_at?: string;
};

export type SaTenantHealth = {
  id: number;
  name: string;
  document?: string | null;
  country_code?: string | null;
  phone_number?: string | null;
  locale?: string | null;
  timezone?: string | null;
  /** Preferência comercial BRL/USD (signup brsend); checkout USD = PayPal F2. */
  preferred_billing_currency?: "BRL" | "USD" | string | null;
  billing_channel?: "self_serve" | "brdominio" | string;
  dns_delegation_mode?: "cname" | "ns" | string;
  auth_dns_nameservers?: string[];
  blocked: boolean;
  blocked_at?: string | null;
  block_reason?: string | null;
  sending_setup_ready: boolean;
  ses_identity_status?: string | null;
  tracking_status?: string | null;
  tracking_ssl_attention?: boolean;
  from_email?: string | null;
  sending_domain?: string | null;
  /** Backend passa boolean; null só por compat com payloads antigos. */
  email_circuit_open: boolean | null;
  email_circuit_open_at?: string | null;
  /** Disparos deste cliente na 2ª+ pausa. */
  sends_awaiting_sa_resume?: number;
  /** Lista curta (até 10) para retomar um a um na ficha. */
  awaiting_sa_sends?: Array<{
    id: number;
    campaign_id: number;
    campaign_name?: string | null;
    subject?: string;
    circuit_pause_count?: number;
    pause_reason?: string | null;
    created_at?: string | null;
  }>;
  /** Último webhook MP (só metadados — sem payload/secret). */
  last_mercadopago_webhook?: {
    id: number;
    external_event_id?: string | null;
    http_status?: number | null;
    processed_at?: string | null;
    created_at?: string | null;
    error_message?: string | null;
  } | null;
  circuit_history?: Array<{
    action: string;
    at?: string | null;
    actor?: string | null;
    meta?: Record<string, unknown> | null;
  }>;
  /** Presente só na resposta do reset de circuit. */
  resumed_sends?: number;
  /** Ramp anti-lista-suja (LAC-088) — stage stored + override + snapshot efetivo. */
  sending_stage?: "new" | "warming" | "trusted" | string;
  daily_send_cap?: number | null;
  /** Valor do soft-cap legado (config) — UI SA. */
  generous_legacy_cap?: number;
  /** Piso SA: 1 e-mail a cada N s — tenant não pode ir mais rápido. */
  min_send_interval_seconds?: number;
  send_interval_seconds?: number;
  sending_ramp?: {
    stage: string;
    sent_today: number;
    daily_cap: number | null;
    remaining_today: number | null;
  } | null;
  owner?: {
    id: number;
    name: string;
    email: string;
    /** LAC-189: true = CTA “desligar 2FA” na ficha SA. */
    mfa_enabled?: boolean;
  } | null;
  /** LAC-192: convite owner ainda não aceito — CTA reenviar na ficha. */
  owner_invite?: {
    pending: boolean;
    email: string;
    expires_at?: string | null;
    updated_at?: string | null;
  } | null;
  /** LAC-193: resultado do enqueue no create/resend (só nessas respostas). */
  invite_mail?: {
    queued: boolean;
    failed: boolean;
    sent?: boolean;
  } | null;
  /** Presente só em create/resend — token em claro uma vez (copiar se mail falhar). */
  invite_accept_url?: string | null;
  subscription: {
    id: number;
    status: string;
    currency: string;
    plan_id?: number;
    emails_used_in_period: number;
    email_quota: number;
    plan_name?: string | null;
    current_period_end?: string | null;
    dunning_entered_at?: string | null;
  } | null;
  /** Histórico leigo dos e-mails da régua (LAC-196 polish). */
  dunning_mails?: Array<{
    kind: string;
    action: string;
    at: string | null;
  }>;
  stats_7d: {
    sent: number;
    bounced: number;
    complained: number;
    failed: number;
    bounce_rate: number;
    complaint_rate: number;
  };
  stats_series_7d?: Array<{
    date: string;
    sent: number;
    bounce_rate: number;
    complaint_rate: number;
  }>;
  created_at?: string;
  /** Binding BYO SES (DOC 14 § 8.4) — só SA. */
  ses_byo?: SaTenantSesByo;
};

export type SaTenantSesByoAccount = {
  region: string;
  configuration_set: string | null;
  status: "draft" | "ready" | "disabled" | string;
  has_access_key: boolean;
  access_key_last4: string | null;
  has_secret: boolean;
  ops_checklist: Record<string, boolean>;
  notes: string | null;
  is_ready: boolean;
  activated_at?: string | null;
  updated_at?: string | null;
  last_health_ok?: boolean | null;
  last_health_at?: string | null;
  last_health_summary?: {
    ok?: boolean;
    checks?: Array<{ name: string; ok: boolean; detail?: string | null }>;
    aws_error_code?: string | null;
    region?: string;
    configuration_set?: string | null;
  } | null;
};

export type SaTenantSesByoHealth = {
  ok: boolean;
  checked_at: string;
  region: string;
  configuration_set: string | null;
  access_key_last4: string | null;
  checks: Array<{ name: string; ok: boolean; detail?: string | null }>;
  aws_error_code: string | null;
};

export type SaTenantSesByoHealthResponse = {
  health: SaTenantSesByoHealth;
  tenant: SaTenantHealth;
};

export type SaTenantSesByoCircuit = {
  available: boolean;
  near_aws_ceiling?: boolean;
  effective: {
    bounce_pct: number;
    complaint_pct: number;
    min_sent: number;
    auto_resume_enabled: boolean;
    source: "platform" | "byo_override" | string;
  };
  values?: {
    using_platform_defaults?: boolean;
  };
  warnings?: Array<{ level: string; code: string; message: string }>;
};

export type SaTenantSesByo = {
  sending_account_mode: "platform" | "customer_aws" | string;
  account: SaTenantSesByoAccount | null;
  /** Limiares efetivos do circuit (read-only na ficha SA). */
  circuit?: SaTenantSesByoCircuit;
};

export type SaTenantSesByoPayload = {
  sending_account_mode?: "platform" | "customer_aws";
  region?: string;
  configuration_set?: string | null;
  status?: "draft" | "ready" | "disabled";
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
  ops_checklist?: Record<string, boolean>;
  notes?: string | null;
};

type Paginated<T> = ApiSuccess<T[]> & {
  meta: PaginatedMeta & {
    protected_domains?: string[];
    protected_listed?: string[];
  };
  links?: unknown;
};

export const getSaDashboardMetrics = (): Promise<
  ApiSuccess<SaDashboardMetrics>
> => axios.get(saUrl("/super-admin/dashboard/metrics"));

export type SaStatsSeriesPoint = {
  date: string;
  sent: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  goal_count: number;
};

export type SaStatsSeries = {
  period_days: number;
  from: string;
  to: string;
  series: SaStatsSeriesPoint[];
};

/** Série diária plataforma — GET /super-admin/dashboard/metrics/series. */
export const getSaDashboardSeries = (params?: {
  days?: number;
}): Promise<ApiSuccess<SaStatsSeries>> =>
  axios.get(saUrl("/super-admin/dashboard/metrics/series"), { params });

export const getSaHostMetrics = (): Promise<ApiSuccess<SaHostMetrics>> =>
  axios.get(saUrl("/super-admin/ops/host-metrics"));

/** Pede amostra urgente — cron no host honra a flag (não lê /proc no container). */
export const requestSaHostSample = (): Promise<
  ApiSuccess<{ requested: boolean; requested_at?: string; hint?: string }>
> => axios.post(saUrl("/super-admin/ops/host-metrics/request-sample"));

/** Janela do ranking de reputação por cliente (LAC-052). */
export type SaReputationWindowStats = {
  sent: number;
  bounced: number;
  hard_bounced?: number;
  soft_bounced?: number;
  complained: number;
  unsubscribed: number;
  failed: number;
  bounce_rate: number;
  hard_bounce_rate?: number;
  soft_bounce_rate?: number;
  complaint_rate: number;
  unsubscribe_rate: number;
};

export type SaReputationCircuitStatus = "ok" | "warning" | "open";

export type SaReputationRow = {
  tenant_id: number;
  tenant_name: string;
  blocked: boolean;
  sending_account_mode?: "platform" | "customer_aws" | string;
  byo_circuit?: {
    bounce_pct: number;
    complaint_pct: number;
    min_sent: number;
    auto_resume_enabled: boolean;
    source: string;
    near_aws_ceiling: boolean;
  } | null;
  circuit_status: SaReputationCircuitStatus;
  email_circuit_open: boolean;
  email_circuit_open_at?: string | null;
  sends_awaiting_sa_resume?: number;
  needs_sa_resume?: boolean;
  at_risk: boolean;
  window_24h: SaReputationWindowStats;
  window_7d: SaReputationWindowStats;
  /** Falhas POST /messages (UTC) — separado de campaign_sends.failed. */
  api_failed_today?: number;
  api_failed_7d?: number;
  /** Mini série 7d hard/soft para sparkline na tabela. */
  sparkline_7d?: Array<{
    date: string;
    hard_bounced: number;
    soft_bounced: number;
  }>;
};

export type SaReputationThresholds = {
  window: "24h" | "7d" | string;
  min_sent: number;
  bounce_rate: number;
  complaint_rate: number;
};

export type SaReputationListResponse = Paginated<SaReputationRow> & {
  thresholds?: SaReputationThresholds;
};

export const getSaReputation = (params?: {
  window?: "24h" | "7d";
  circuit_open?: boolean | 1 | 0 | string;
  at_risk?: boolean | 1 | 0 | string;
  needs_sa_resume?: boolean | 1 | 0 | string;
  byo_near_ceiling?: boolean | 1 | 0 | string;
  search?: string;
  sort?: "risk" | "bounce_rate" | "complaint_rate" | "sent" | "name";
  page?: number;
  per_page?: number;
}): Promise<SaReputationListResponse> =>
  axios.get(saUrl("/super-admin/reputation"), { params });

export type SaReputationExportFilters = {
  window?: "24h" | "7d";
  circuit_open?: boolean | 1 | 0 | string;
  at_risk?: boolean | 1 | 0 | string;
  needs_sa_resume?: boolean | 1 | 0 | string;
  byo_near_ceiling?: boolean | 1 | 0 | string;
  search?: string;
  sort?: "risk" | "bounce_rate" | "complaint_rate" | "sent" | "name";
};

export type SaReputationExportStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | string;

/** Sessão de export CSV (sync 200 ou async 202 + poll). */
export type SaReputationExportSession = {
  id: number;
  status: SaReputationExportStatus;
  mode?: "sync" | "async" | string;
  row_count?: number;
  download_ready?: boolean;
  error_message?: string | null;
};

/** POST /super-admin/reputation/export — 200 sync ou 202 async. */
export const exportSaReputation = (
  filters?: SaReputationExportFilters
): Promise<ApiSuccess<SaReputationExportSession>> =>
  axios.post(saUrl("/super-admin/reputation/export"), filters || {});

/** GET /super-admin/reputation/exports/{id} — poll até completed. */
export const getSaReputationExport = (
  id: number
): Promise<ApiSuccess<SaReputationExportSession>> =>
  axios.get(saUrl(`/super-admin/reputation/exports/${id}`));

/**
 * Download CSV com JWT (blob).
 * Interceptor devolve response.data (= Blob); Content-Disposition não chega aqui.
 */
export const downloadSaReputationExport = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const data = await axios.get(
    saUrl(`/super-admin/reputation/exports/${id}/download`),
    { responseType: "blob" }
  );

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  const today = new Date().toISOString().slice(0, 10);
  anchor.download = fallbackFilename || `reputation-${today}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/** Linha sanitizada de webhook SES (GET /super-admin/webhook-logs). */
export type SaWebhookLog = {
  id: number;
  provider_slug: string;
  direction?: string | null;
  http_status?: number | null;
  external_event_id?: string | null;
  tenant_id?: number | null;
  tenant_name?: string | null;
  event_type?: string | null;
  /** Permanent | Transient | Undetermined — só em Bounce (LAC-117 polish). */
  bounce_type?: "Permanent" | "Transient" | "Undetermined" | string | null;
  mail_message_id?: string | null;
  error_message?: string | null;
  processed_at?: string | null;
  created_at?: string | null;
};

export const getSaWebhookLogs = (params?: {
  provider_slug?: string;
  tenant_id?: number;
  only_errors?: boolean | 1 | 0 | string;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SaWebhookLog>> =>
  axios.get(saUrl("/super-admin/webhook-logs"), { params });

export type SaInactiveRankingRow = {
  tenant_id: number;
  tenant_name: string;
  active_count: number;
  eligible_count: number;
  eligible_percent: number;
  days: number;
};

export const getSaInactiveRanking = (params?: {
  days?: number;
  min_percent?: number;
}): Promise<
  ApiSuccess<SaInactiveRankingRow[]> & {
    meta?: { days: number; min_percent: number; limit: number };
  }
> => axios.get(saUrl("/super-admin/tenants/inactive-ranking"), { params });

export type SaSendingListResponse = Paginated<SaCampaignSend> & {
  queue?: SaQueueSnapshot;
};

export const getSaSending = (params?: {
  status?: string;
  pause_reason?: string;
  /** hard = inválido; soft = temporário. */
  bounce_kind?: "hard" | "soft" | "";
  tenant_id?: number;
  /** LAC-142: dia UTC YYYY-MM-DD (spark da ficha). */
  date?: string;
  needs_sa_resume?: boolean | 1 | 0;
  /** 1ª pausa elegível a auto-retomada. */
  auto_resume_eligible?: boolean | 1 | 0;
  active_partial_chain?: boolean | 1 | 0;
  page?: number;
  per_page?: number;
}): Promise<SaSendingListResponse> =>
  axios.get(saUrl("/super-admin/sending"), { params });

export const resumeSaSend = (
  id: number
): Promise<ApiSuccess<SaCampaignSend>> =>
  axios.post(saUrl(`/super-admin/sending/${id}/resume`));

/** Libera claims stale (api_messages + recipients) — ops quando P0 travou. */
export type SaReleaseStaleClaimsResult = {
  api_messages: number;
  recipients: number;
};

export const releaseSaStaleClaims = (params?: {
  dry_run?: boolean;
}): Promise<ApiSuccess<SaReleaseStaleClaimsResult>> =>
  axios.post(saUrl("/super-admin/sending/release-stale-claims"), {
    dry_run: params?.dry_run === true,
  });

/** Suspende/reativa, troca canal ou edita ficha+owner (PATCH — LAC-076). */
export const patchSaTenant = (
  id: number,
  payload: {
    blocked?: boolean;
    block_reason?: string | null;
    billing_channel?: "self_serve" | "brdominio" | string;
    dns_delegation_mode?: "cname" | "ns" | string;
    name?: string;
    document?: string | null;
    country_code?: string | null;
    phone_number?: string | null;
    locale?: string | null;
    timezone?: string | null;
    plan_id?: number;
    owner_name?: string;
    owner_email?: string;
    owner_password?: string | null;
  }
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.patch(saUrl(`/super-admin/tenants/${id}`), payload);

export type SaCreateTenantPayload = {
  name: string;
  owner_name: string;
  owner_email: string;
  // LAC-074: opcional quando send_invite=true — senha definida via link.
  owner_password?: string;
  plan_id: number;
  billing_channel: "self_serve" | "brdominio" | string;
  document?: string | null;
  country_code?: string | null;
  phone_number?: string | null;
  locale?: string | null;
  timezone?: string | null;
  // LAC-074: se true, dispara e-mail de convite em vez de definir senha.
  send_invite?: boolean;
};

/** Provisiona cliente + owner + assinatura sem gateway (LAC-053). */
export const createSaTenant = (
  payload: SaCreateTenantPayload
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.post(saUrl("/super-admin/tenants"), payload);

/** LAC-192: reenvia link de definir senha (novo token; invalida o anterior). */
export const resendSaOwnerInvite = (
  id: number,
  payload?: { send_email?: boolean }
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/owner-invite/resend`), payload || {});

/** LAC-189: recovery one-shot (padrão) ou desliga MFA do owner. */
export const resetSaOwnerMfa = (
  id: number,
  mode: "recovery" | "disable" = "recovery"
): Promise<
  ApiSuccess<
    SaTenantHealth & {
      mfa_reset?: {
        mode: string;
        recovery_codes?: string[];
      };
    }
  >
> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/owner/mfa/reset`), {
    confirm: true,
    mode,
  });

/** JWT tenant de suporte — sem refresh_token (sessão curta). */
export type SaImpersonateResult = {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: Record<string, unknown>;
  tenant: Record<string, unknown>;
};

export const impersonateSaTenant = (
  id: number,
  payload?: { user_id?: number }
): Promise<ApiSuccess<SaImpersonateResult>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/impersonate`), payload || {});

/** Reset exige playbook_confirmed (LAC-141) — evita clique acidental. */
export const resetSaEmailCircuit = (
  id: number,
  payload?: { playbook_confirmed: boolean; playbook_note?: string }
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/email-circuit/reset`), payload || {});

/** Recria identity SES quando status = failed (opcional force). */
export const recreateSaSesIdentity = (
  id: number,
  force?: boolean
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/ses-identity/recreate`), {
    ...(force ? { force: true } : {}),
  });

/** Força estágio / override de cap / piso de ritmo (DOC 14 · PATCH sending-ramp). */
export type SaSendingRampPayload = {
  sending_stage?: "new" | "warming" | "trusted" | string;
  /** null remove override e volta ao default do estágio. */
  daily_send_cap?: number | null;
  /** Soft-cap legado sem promover a trusted. */
  generous_legacy_cap?: boolean;
  /** Piso SA em segundos (0 = sem piso). */
  min_send_interval_seconds?: number | null;
};

export const patchSaTenantSendingRamp = (
  id: number,
  payload: SaSendingRampPayload
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.patch(saUrl(`/super-admin/tenants/${id}/sending-ramp`), payload);

/** Binding conta AWS do cliente (BYO) — PATCH ses-account. */
export const patchSaTenantSesAccount = (
  id: number,
  payload: SaTenantSesByoPayload
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.patch(saUrl(`/super-admin/tenants/${id}/ses-account`), payload);

/** Probe IAM + Configuration Set na conta AWS do cliente (não envia e-mail). */
export const postSaTenantSesAccountHealth = (
  id: number
): Promise<ApiSuccess<SaTenantSesByoHealthResponse>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/ses-account/health`));

/** Restaura limiares BYO ao padrão da plataforma (sugestão ops). */
export const postSaTenantByoCircuitResetRecommended = (
  id: number
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.post(saUrl(`/super-admin/tenants/${id}/byo-circuit/reset-recommended`));

export const clearSaPlatformFreeze = (): Promise<
  ApiSuccess<SaDashboardMetrics>
> => axios.post(saUrl("/super-admin/platform-freeze/clear"));

export const getSaEmailSuppressions = (params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SaEmailSuppression>> =>
  axios.get(saUrl("/super-admin/email-suppressions"), { params });

export const createSaEmailSuppression = (payload: {
  email: string;
  reason?: string;
}): Promise<ApiSuccess<SaEmailSuppression>> =>
  axios.post(saUrl("/super-admin/email-suppressions"), payload);

export const deleteSaEmailSuppression = (
  id: number
): Promise<ApiSuccess<null>> =>
  axios.delete(saUrl(`/super-admin/email-suppressions/${id}`));

export const getSaDomainSuppressions = (params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SaDomainSuppression>> =>
  axios.get(saUrl("/super-admin/domain-suppressions"), { params });

/** Linha de activity_logs (GET /super-admin/activity-logs — LAC-065). */
export type SaActivityLog = {
  id: number;
  action: string;
  tenant_id: number | null;
  tenant_name: string | null;
  actor_type: string | null;
  actor_id: number | null;
  actor_label: string | null;
  impersonator_id: number | null;
  route: string | null;
  ip: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: string | null;
};

export const getSaActivityLogs = (params?: {
  action?: string;
  tenant_id?: number;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SaActivityLog>> =>
  axios.get(saUrl("/super-admin/activity-logs"), { params });

export const createSaDomainSuppression = (payload: {
  domain: string;
  reason?: string;
}): Promise<ApiSuccess<SaDomainSuppression>> =>
  axios.post(saUrl("/super-admin/domain-suppressions"), payload);

export const deleteSaDomainSuppression = (
  id: number
): Promise<ApiSuccess<null>> =>
  axios.delete(saUrl(`/super-admin/domain-suppressions/${id}`));

export const getSaTenants = (params?: {
  search?: string;
  blocked?: string;
  billing_channel?: string;
  preferred_billing_currency?: string;
  at_daily_cap?: string;
  /** Clientes com assinatura active/past_due neste plano (deep-link do modal planos). */
  plan_id?: number;
  page?: number;
  per_page?: number;
}): Promise<Paginated<SaTenantListItem>> =>
  axios.get(saUrl("/super-admin/tenants"), { params });

export const getSaTenant = (
  id: number
): Promise<ApiSuccess<SaTenantHealth>> =>
  axios.get(saUrl(`/super-admin/tenants/${id}`));

/** Taxa API % 0–100 → exibição. */
export const formatSaRate = (
  value: number | null | undefined,
  locale?: string
): string => {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return (
    Number(value).toLocaleString(locale || undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + "%"
  );
};

/** Status do catálogo SES (tenant nunca vê isto — só SA/ops). */
export type SaSesEndpointStatus = "active" | "disabled" | "blocked";

/** Settings globais SES — secret nunca vem cru (LAC-043). */
export type SaSesSettings = {
  safety_ceiling_bps: number;
  reserved_p0_daily: number;
  p0_sent_today: number;
  p0_remaining_today: number;
  campaign_cap_today: number | null;
  campaign_sent_today: number;
  campaign_remaining_today: number | null;
  total_effective_quota_24h: number;
  has_access_key: boolean;
  has_secret: boolean;
  access_key_last4: string | null;
  platform_send_frozen: boolean;
  webhook_url: string;
};

export type SaSesEndpoint = {
  id: number;
  region: string;
  label: string;
  status: SaSesEndpointStatus;
  is_primary: boolean;
  quota_24h: number;
  max_per_second: number;
  safety_ceiling_bps: number | null;
  effective_safety_ceiling_bps: number;
  effective_quota_24h: number;
  effective_max_per_second: number;
  configuration_set: string | null;
  sort_order: number;
  usage_24h: number;
  usage_24h_ratio_bps: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type SaSesEndpointPayload = {
  region?: string;
  label: string;
  status?: SaSesEndpointStatus;
  is_primary?: boolean;
  quota_24h: number;
  max_per_second?: number;
  safety_ceiling_bps?: number | null;
  configuration_set?: string | null;
  sort_order?: number;
};

export type SaSesSettingsPayload = {
  safety_ceiling_bps?: number;
  reserved_p0_daily?: number;
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
};

export const getSaSesSettings = (): Promise<ApiSuccess<SaSesSettings>> =>
  axios.get(saUrl("/super-admin/ses/settings"));

export const updateSaSesSettings = (
  payload: SaSesSettingsPayload
): Promise<ApiSuccess<SaSesSettings>> =>
  axios.patch(saUrl("/super-admin/ses/settings"), payload);

export const getSaSesEndpoints = (): Promise<ApiSuccess<SaSesEndpoint[]>> =>
  axios.get(saUrl("/super-admin/ses/endpoints"));

export const createSaSesEndpoint = (
  payload: SaSesEndpointPayload & { region: string }
): Promise<ApiSuccess<SaSesEndpoint>> =>
  axios.post(saUrl("/super-admin/ses/endpoints"), payload);

export const updateSaSesEndpoint = (
  id: number,
  payload: Partial<SaSesEndpointPayload>
): Promise<ApiSuccess<SaSesEndpoint>> =>
  axios.patch(saUrl(`/super-admin/ses/endpoints/${id}`), payload);

export const deleteSaSesEndpoint = (
  id: number
): Promise<ApiSuccess<null>> =>
  axios.delete(saUrl(`/super-admin/ses/endpoints/${id}`));

/** bps (9500) → % para formulário (95). */
export const bpsToPercent = (bps: number | null | undefined): number =>
  Math.round((Number(bps) || 0) / 100);

/** % do formulário → bps para a API. */
export const percentToBps = (percent: number): number =>
  Math.round(Number(percent) * 100);

/** Settings PayPal globais (F2 billing USD) — secret nunca vem cru. */
export type SaPayPalSettings = {
  enabled: boolean;
  mode: "sandbox" | "live";
  has_client_id: boolean;
  client_id_last4: string | null;
  has_secret: boolean;
  has_webhook_id: boolean;
  webhook_id_last4: string | null;
  webhook_url: string;
};

export type SaPayPalSettingsPayload = {
  enabled?: boolean;
  mode?: "sandbox" | "live";
  client_id?: string;
  secret?: string;
  webhook_id?: string;
};

/** GET /super-admin/billing/paypal — settings PayPal globais. */
export const getSaPayPalSettings = (): Promise<ApiSuccess<SaPayPalSettings>> =>
  axios.get(saUrl("/super-admin/billing/paypal"));

/** PATCH /super-admin/billing/paypal — atualiza credenciais PayPal. */
export const updateSaPayPalSettings = (
  payload: SaPayPalSettingsPayload
): Promise<ApiSuccess<SaPayPalSettings>> =>
  axios.patch(saUrl("/super-admin/billing/paypal"), payload);

/** POST /super-admin/billing/paypal/test — probe OAuth PayPal (não cobra). */
export const testSaPayPalConnection = (): Promise<ApiSuccess<null>> =>
  axios.post(saUrl("/super-admin/billing/paypal/test"));
